import { useParams } from 'react-router-dom';
import { Form, FormProps } from 'components';
import { getUpdatedPayload } from 'utils/helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAttachmentFileBulkCreateMutation, useNoteCreateMutation, useNoteUpdateMutation } from 'api';
import { RelationDataType } from './relation/RelationCard';
import { INoteAddUpdateModal } from './index';

export interface SupplierCommunicationHubNoteFieldType {
  entityId: string;
  note: string;
  entityName: string;
  relations?: Record<string, any>;
  attachmentTypes: string[];
  noteId?: string;
  file?: any;
}

interface InputItem {
  id: string;
  relationEntity: string;
  relationType: string;
}

interface RelationsOutput {
  [relationType: string]: string[];
}

const groupRelationsByType = (inputItems: InputItem[]): RelationsOutput => {
  const relations: RelationsOutput = {};
  inputItems.forEach(({ relationEntity, relationType }) => {
    if (!relations[relationType]) {
      relations[relationType] = [];
    }
    relations[relationType].push(relationEntity.trim());
  });
  return relations;
};

const diffAttachments = (current: Array<{ id?: string; type: string; file: any }>, original: Array<{ id: string }>) => {
  const attachmentsToAdd = current.filter((att) => !att.id);
  const originalIds = original.map((att) => att.id);
  const currentIdSet = new Set(current.flatMap((att) => (att.id ? [att.id] : [])));
  const deletedAttachmentIds = originalIds.filter((id) => !currentIdSet.has(id));
  return { attachmentsToAdd, deletedAttachmentIds };
};

export const useNoteAddEdit = (props: INoteAddUpdateModal) => {
  const { dataToEdit, onModalStateChange, modalState, entity } = props;
  const [relations, setRelations] = useState<RelationDataType[]>([]);
  const [relationEdit, setRelationEdit] = useState<Record<string, string> | null>(null);
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<any[]>([]);
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<{ type: string; file: any }[]>([]);

  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const { handleError, handleSuccess } = useNotificationMessages();

  const [createNote] = useNoteCreateMutation();
  const [updateNote] = useNoteUpdateMutation();
  const [createBulkFiles] = useAttachmentFileBulkCreateMutation();
  const { attachmentsToAdd, deletedAttachmentIds } = useMemo(
    () =>
      dataToEdit
        ? diffAttachments(attachments, dataToEdit.attachments)
        : { attachmentsToAdd: attachments, deletedAttachmentIds: [] },
    [attachments, dataToEdit]
  );

  const onRelationItemSave = useCallback(
    (relation: any) => {
      const hasError = form.getFieldError('relations')?.length;
      if (hasError) {
        form.setFields([
          {
            name: 'relations',
            errors: [],
          },
        ]);
      }
      setRelationEdit(null);
      setRelations(relation);
    },
    [form]
  );
  const onRelationItemCancel = useCallback(() => setRelationEdit(null), []);
  const onRelationItemRemove = useCallback((relation: any) => {
    setRelations(relation);
    setDisableSaveBtn(false);
  }, []);
  const handleAttachmentChange = useCallback((attachments: { type: string; file: File }[]) => {
    setAttachments(attachments);
    setDisableSaveBtn(false);
  }, []);
  const onRelationItemEdit = useCallback(
    (id: string, type: string) =>
      setRelationEdit({
        entity: id,
        type,
      }),
    []
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    setRelations([]);
    setAttachments([]);
    setFileList([]);
    setAttachmentType(undefined);
    setDisableSaveBtn(true);
    setRelationEdit(null);
    onModalStateChange(false);
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SupplierCommunicationHubNoteFieldType>['onFinish'] = useCallback(
    async (values: SupplierCommunicationHubNoteFieldType) => {
      const base = getUpdatedPayload(values, dataToEdit);
      const payload: any = {
        ...base,
        file: undefined,
        entityName: entity,
        entityId: id,
        relations: { [entity]: [id], ...groupRelationsByType(relations) },
        attachmentTypes: attachmentsToAdd.map((att) => att.type),
        ...(dataToEdit && { noteId: dataToEdit.id }),
        ...(deletedAttachmentIds.length > 0 && { deletedAttachmentIds: deletedAttachmentIds }),
      };

      const action = dataToEdit ? updateNote : createNote;
      try {
        setIsLoading(true);
        const { data, error } = await action(payload);
        if (!data?.success) return handleError(error);

        if (attachmentsToAdd.length && Array.isArray(data.data)) {
          const form = new FormData();
          form.append('attachmentIds', data.data.join(','));
          attachmentsToAdd.forEach((att) => form.append('files', att.file));
          form.append('serviceName', 'WRD');
          await createBulkFiles({ data: form, entityName: 'COMMUNICATION_HUB' });
        }
        handleSuccess(`The note was successfully ${dataToEdit ? 'updated' : 'added'}.`);
        onCancel();
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [
      createNote,
      updateNote,
      handleSuccess,
      handleError,
      dataToEdit,
      onCancel,
      id,
      relations,
      createBulkFiles,
      attachmentsToAdd,
      deletedAttachmentIds,
      entity,
    ]
  );

  useEffect(() => {
    if (dataToEdit && modalState) {
      const attachmentEdit = dataToEdit?.attachments?.map((_attachment: any) => ({
        ..._attachment,
        type: _attachment.attachmentType,
        file: { name: _attachment.fileName },
      }));
      const relationEdit = Object.entries(dataToEdit.relations)
        .filter(([_entity]) => _entity !== entity)
        .flatMap(([entity, values]: any) =>
          values?.map((v: any) => ({
            relationType: entity,
            relationEntity: v.name ? v.id : v.uuid,
          }))
        );
      form.setFieldsValue({ note: dataToEdit.note });
      setRelations(relationEdit);
      setAttachments(attachmentEdit);
    }
  }, [dataToEdit, modalState, form, entity]);

  return {
    onFinish,
    onCancel,
    form,
    onRelationItemSave,
    isLoading,
    attachmentType,
    relations,
    relationEdit,
    onRelationItemCancel,
    onRelationItemRemove,
    onRelationItemEdit,
    setAttachmentType,
    attachments,
    setAttachments,
    fileList,
    setFileList,
    disableSaveBtn,
    handleAttachmentChange,
  };
};
